import { ChangeEvent, useEffect, useState, useRef, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../../../../../libs/ui-kit/src/lib/icon/icon'; // Ensure this path is correct and the Icon component is exported properly
import { Text } from '../../../../../libs/ui-kit/src/lib/text/text'; // Adjusted import path

export interface Country {
    code: string;
    name: string;
    flag: string;
}

// Validation rules and formatting (assuming this remains largely the same)
const taxValidationRules: Record<string, TaxRule> = {
    ar: {
        regex: /^\d{2}-\d{8}-\d{1}$/,
        placeholder: 'XX-XXXXXXXX-X',
        maxLength: 13,
        format: (value: string) => {
            const digits = value.replace(/[^0-9]/g, '');
            if (digits.length <= 2) {
                return digits;
            }
            if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
            return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10, 11)}`;
        },
        validate: (value: string) => /^\d{2}-\d{8}-\d{1}$/.test(value),
    },
    uy: {
        regex: /^\d{9,12}$/,
        placeholder: 'XXXXXXXXXXXX',
        maxLength: 12,
        format: (value: string) => value.replace(/[^0-9]/g, '').slice(0, 12),
        validate: (value: string) => /^\d{9,12}$/.test(value),
    },
    py: {
        regex: /^\d{8,}-\d{1}$/,
        placeholder: 'XXXXXXXX-X',
        maxLength: 13,
        format: (value: string) => {
            const digits = value.replace(/[^0-9]/g, '');
            if (digits.length <= 8) return digits;
            return `${digits.slice(0, -1)}-${digits.slice(-1, 13)}`;
        },
        validate: (value: string) => /^\d{8,}-\d{1}$/.test(value),
    },
    cl: {
        regex: /^\d{1,2}\.\d{3}\.\d{3}-(\d|[A-z]){1}$/,
        placeholder: 'XX.XXX.XXX-X',
        maxLength: 12,
        format: (value: string) => {
            // Remove all non-digit and non-k/k/K characters
            const cleaned = value.replace(/[^0-9kK]/g, '').toUpperCase();

            // Separate digits and possible K at the end
            let digits = cleaned.replace(/[^0-9]/g, '');

            // Limit digits to 8 (max for Chilean RUT)
            digits = digits.slice(0, 8);
            // Add dots for thousands
            let formatted = '';
            if (digits.length > 0) {
                if (digits.length <= 3) {
                    formatted = digits;
                } else if (digits.length <= 6) {
                    formatted = `${digits.slice(0, digits.length - 3)}.${digits.slice(-3)}`;
                } else {
                    formatted = `${digits.slice(0, digits.length - 6)}.${digits.slice(
                        -6,
                        digits.length - 3,
                    )}.${digits.slice(-3)}`;
                }
            }

            // Add hyphen and verifier if present
            if (digits.length === 8 && cleaned.length > 8) {
                formatted += `-${cleaned.charAt(8)}`;
            }

            return formatted;
        },
        validate: (value: string) => /^\d{1,2}\.\d{3}\.\d{3}-(\d|[A-z]){1}$/.test(value),
    },
    mx: {
        regex: /^[A-Z&Ñ]{3,4}\d{6}[A-Z\d]{3}$/,
        placeholder: 'ABC9301231A2',
        maxLength: 13,
        format: (value: string) =>
            value
                .toUpperCase()
                .replace(/[^A-Z0-9&Ñ]/g, '')
                .slice(0, 13),
        validate: (value: string) => /^[A-Z&Ñ]{3,4}\d{6}[A-Z\d]{3}$/.test(value),
    },
    es: {
        regex: /^[A-Z]\d{8}$/,
        placeholder: 'A12345678',
        maxLength: 9,
        format: (value: string) => {
            const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (!cleaned) return '';
            const letter = cleaned.charAt(0).replace(/[^A-Z]/, '');
            const numbers = cleaned.substring(1).replace(/[^0-9]/g, '');
            return `${letter}${numbers}`.slice(0, 9);
        },
        validate: (value: string) => /^[A-Z]\d{8}$/.test(value),
    },
    bo: {
        regex: /^\d{7,10}-\d{3}$/,
        placeholder: 'XXXXXXX-XXX',
        maxLength: 14,
        format: (value: string) => {
            const digits = value.replace(/[^0-9]/g, '');
            // Solo mostrar números si tenemos 10 o menos dígitos
            if (digits.length < 10) return digits;
            // Si tenemos más de 10 dígitos, formatear con guión
            if (digits.length <= 13) {
                const beforeDash = digits.slice(0, -3);
                const afterDash = digits.slice(-3);
                return `${beforeDash}-${afterDash}`;
            }
            // Limitar a máximo 13 dígitos (10 + 3)
            return `${digits.slice(0, 10)}-${digits.slice(10, 13)}`;
        },
        validate: (value: string) => /^\d{7,10}-\d{3}$/.test(value),
    },
    bz: {
        regex: /^\d{6,7}$/,
        placeholder: 'XXXXXXX',
        maxLength: 7,
        format: (value: string) => value.replace(/[^0-9]/g, '').slice(0, 7),
        validate: (value: string) => /^\d{6,7}$/.test(value),
    },
    cr: {
        // Cédula física (9), jurídica (10), DIMEX (11-12) o NITE (10)
        regex: /^\d{9,12}$/,
        placeholder: 'XXXXXXXXXX',
        maxLength: 12,
        format: (value: string) => value.replace(/[^0-9]/g, '').slice(0, 12),
        validate: (value: string) => /^\d{9,12}$/.test(value),
    },
    sv: {
        // NIT (14 dígitos) o DUI (9 dígitos)
        regex: /^(\d{4}-\d{6}-\d{3}-\d|\d{8}-\d)$/,
        placeholder: 'XXXX-XXXXXX-XXX-X',
        maxLength: 17,
        format: (value: string) => {
            const digits = value.replace(/[^0-9]/g, '').slice(0, 14);
            if (digits.length <= 8) return digits;
            // DUI: XXXXXXXX-X
            if (digits.length === 9) return `${digits.slice(0, 8)}-${digits.slice(8)}`;
            // NIT: XXXX-XXXXXX-XXX-X
            if (digits.length <= 10) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
            if (digits.length <= 13) return `${digits.slice(0, 4)}-${digits.slice(4, 10)}-${digits.slice(10)}`;
            return `${digits.slice(0, 4)}-${digits.slice(4, 10)}-${digits.slice(10, 13)}-${digits.slice(13)}`;
        },
        validate: (value: string) => /^(\d{4}-\d{6}-\d{3}-\d|\d{8}-\d)$/.test(value),
    },
    gt: {
        // NIT o CUI; el verificador (último caracter) puede ser un dígito o K
        regex: /^\d{1,12}-[\dK]$/,
        placeholder: 'XXXXXXX-X',
        maxLength: 14,
        format: (value: string) => {
            const cleaned = value.toUpperCase().replace(/[^0-9K]/g, '');
            // La K solo puede ser el verificador: se descarta cualquier K que no esté al final
            const verifier = cleaned.slice(-1);
            const digits = cleaned.slice(0, -1).replace(/K/g, '').slice(0, 12);
            if (!digits) return verifier === 'K' ? '' : verifier;
            return `${digits}-${verifier}`;
        },
        validate: (value: string) => /^\d{1,12}-[\dK]$/.test(value),
    },
    hn: {
        regex: /^\d{4}-\d{4}-\d{6}$/,
        placeholder: 'XXXX-XXXX-XXXXXX',
        maxLength: 16,
        format: (value: string) => {
            const digits = value.replace(/[^0-9]/g, '').slice(0, 14);
            if (digits.length <= 4) return digits;
            if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
            return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
        },
        validate: (value: string) => /^\d{4}-\d{4}-\d{6}$/.test(value),
    },
    ni: {
        // Persona jurídica: letra + 13 dígitos. Persona natural (cédula): 13 dígitos + letra
        regex: /^([A-Z]\d{13}|\d{13}[A-Z])$/,
        placeholder: 'J0310000000000',
        maxLength: 14,
        format: (value: string) =>
            value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .slice(0, 14),
        validate: (value: string) => /^([A-Z]\d{13}|\d{13}[A-Z])$/.test(value),
    },
    pa: {
        // Estructura variable (cédula o datos del Registro Público) con DV opcional de 2 dígitos
        regex: /^[A-Z0-9]{1,10}(-[A-Z0-9]{1,10}){1,3}( DV \d{2})?$/,
        placeholder: '8-100-678 DV 90',
        maxLength: 40,
        format: (value: string) =>
            value
                .toUpperCase()
                .replace(/[^A-Z0-9\- ]/g, '')
                .replace(/\s+/g, ' ')
                .slice(0, 40),
        validate: (value: string) => /^[A-Z0-9]{1,10}(-[A-Z0-9]{1,10}){1,3}( DV \d{2})?$/.test(value),
    },
};

interface TaxRule {
    regex: RegExp;
    placeholder: string;
    maxLength: number;
    format: (value: string) => string;
    validate: (value: string) => boolean;
}

export interface TaxInputProps {
    id?: string;
    name?: string;
    label?: string;
    value?: string;
    onChange: (value: string, isValid: boolean, country: string) => void;
    onBlur?: (value: string, isValid: boolean, country: string) => void;
    onCountryChange?: (country: string) => void;
    availableCountries: Country[];
    currentCountryCode: string;
    defaultCountry?: string;
    error?: boolean;
    errorMessage?: string | null;
    disabled?: boolean;
    readonly?: boolean;
    required?: boolean;
    rounded?: 'standard' | 'full';
    style?: 'primary' | 'variant';
    classNameInput?: string;
    classNameContainer?: string;
    containerDataTestId?: string;
    'data-testid'?: string;
    'data-test-id'?: string;
}

export function TaxInput({
    id,
    name,
    label,
    currentCountryCode,
    value = '',
    availableCountries = [],
    onChange,
    onBlur,
    onCountryChange,
    error,
    errorMessage,
    disabled,
    readonly,
    required,
    rounded = 'standard',
    style = 'primary',
    classNameInput,
    classNameContainer,
    containerDataTestId,
    'data-testid': dataTestId,
}: TaxInputProps) {
    const countryCodes = useMemo(() => availableCountries.map((c) => c.code.toLowerCase()), [availableCountries]);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(
        taxValidationRules[currentCountryCode]?.placeholder || '',
    );
    const [currentMaxLength, setCurrentMaxLength] = useState<number | undefined>(50);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const activeCountries = availableCountries.filter((c) => countryCodes.includes(c.code));
    const currentCountry = activeCountries.find((c) => c.code === currentCountryCode) || activeCountries[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const processInputChange = (val: string, country: string, isCountryOrPropChange = false) => {
        const rule = taxValidationRules[country];
        let processedValue = val;
        let isValid = false;

        if (rule) {
            if (rule.format) {
                processedValue = rule.format(val);
            }
            if (rule.maxLength && processedValue.length > rule.maxLength) {
                processedValue = processedValue.slice(0, rule.maxLength);
            }
            isValid = rule.validate(processedValue);
        } else {
            // For countries not in rules (if any allowed by onlyCountries but not in taxValidationRules)
            isValid = true; // Or some default validation
        }

        // Only call onChange if the processed value is different from the prop value,
        // or if it's a direct result of a country change or initial prop setting.
        if (processedValue !== value || isCountryOrPropChange) {
            onChange(processedValue, isValid, country);
        }
    };

    const handleDirectInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        processInputChange(e.target.value, currentCountryCode);
    };

    const handleInputBlur = () => {
        const rule = taxValidationRules[currentCountryCode];
        let isValid = false;
        if (rule) {
            isValid = rule.validate(value);
        } else {
            isValid = true; // Default for unsupported countries
        }
        if (onBlur) {
            onBlur(value, isValid, currentCountryCode);
        }
    };

    const handleCountrySelect = (countryCode: string) => {
        if (onChange) {
            onChange('', false, countryCode);
        }

        const rule = taxValidationRules[countryCode];
        if (rule) {
            setCurrentPlaceholder(rule.placeholder);
            setCurrentMaxLength(rule.maxLength);
        } else {
            setCurrentPlaceholder('');
            setCurrentMaxLength(50); // Default max length
        }
        if (onCountryChange) {
            onCountryChange(countryCode);
        }
        setIsDropdownOpen(false);
    };

    const stylesColors = { primary: 'focus:border-primary-500', variant: 'focus:border-variant-800' };

    const mergedInputClasses = twMerge(
        'w-full outline-none text-sm placeholder-gray-300 py-2 pl-2',
        rounded === 'standard' ? 'rounded-r' : 'rounded-r-full', // Input is on the right
        'border-l-0', // No left border for the input itself
        !error && 'border-gray-100 hover:border-zinc-500 active:border-zinc-500',
        stylesColors[style],
        error && '!border-error-200 hover:!border-error-200 active:!border-error-200 focus:!border-error-200',
        (disabled || readonly) &&
            'border-gray-50 hover:border-gray-50 active:border-gray-50 focus:border-gray-50 bg-gray-50 !cursor-default',
        classNameInput,
    );

    const containerClasses = twMerge(
        'flex items-center border',
        rounded === 'standard' ? 'rounded' : 'rounded-full',
        !error && 'border-gray-100 focus-within:border-zinc-500',
        stylesColors[style], // Apply focus styles to container
        error && '!border-error-200 focus-within:!border-error-200',
        (disabled || readonly) && 'border-gray-50 bg-gray-50',
        classNameContainer,
    );

    return (
        <div data-test-id={containerDataTestId} className={twMerge('relative', classNameContainer)}>
            {label && (
                <div className="flex mb-1">
                    <Text element="label" lineHeight="24" weight="medium" htmlFor={id}>
                        {label}
                    </Text>
                    {required && <span className="text-red-600">&nbsp;*</span>}
                </div>
            )}
            <div className={containerClasses} data-testid={dataTestId || `${name}-tax-input-container`}>
                <div className="relative  self-stretch" ref={dropdownRef}>
                    <button
                        type="button"
                        className={twMerge(
                            'flex items-center justify-center h-full px-2 focus:outline-1 focus:outline-gray-300 border-r border-gray-200 bg-gray-50 focus:bg-white',
                            rounded === 'standard' ? 'rounded-l' : 'rounded-l-full', // Add left rounding
                            (disabled || readonly) && 'cursor-default',
                        )}
                        onClick={() => !disabled && !readonly && setIsDropdownOpen(!isDropdownOpen)}
                        disabled={disabled || readonly}
                        aria-haspopup="listbox"
                        aria-expanded={isDropdownOpen}
                    >
                        {currentCountry && (
                            <img src={currentCountry.flag} alt={currentCountry.name} className="w-5 h-auto mr-1" />
                        )}
                        <Icon
                            name="ri-arrow-down-s-fill" // Make sure this matches an available icon in your icon set
                            size={14}
                            color="text-gray-500"
                            className={twMerge(
                                'transition-transform duration-200',
                                'origin-center', // Ensures transform origin is the center
                                isDropdownOpen ? 'rotate-180' : '',
                            )}
                        />
                    </button>
                    {isDropdownOpen && (
                        <div
                            className="absolute z-10 mt-1 w-max bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-auto"
                            role="listbox"
                        >
                            {activeCountries.map((countryItem) => (
                                <button
                                    key={countryItem.code}
                                    className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer w-full"
                                    onClick={() => handleCountrySelect(countryItem.code)}
                                    role="option"
                                    aria-selected={currentCountryCode === countryItem.code}
                                >
                                    <img src={countryItem.flag} alt={countryItem.name} className="w-5 h-auto mr-2" />
                                    <Text>{countryItem.name}</Text> {/* Corrected size prop */}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <input
                    id={id}
                    name={name}
                    type="text"
                    value={value}
                    onChange={handleDirectInputChange}
                    onBlur={handleInputBlur}
                    placeholder={currentPlaceholder}
                    maxLength={currentMaxLength}
                    disabled={disabled}
                    readOnly={readonly}
                    required={required}
                    className={mergedInputClasses} // Apply specific input classes
                    data-testid={dataTestId || `${name}-tax-input`}
                />
            </div>
            {error && errorMessage && <p className="text-red-500 text-[10px] mt-1">{errorMessage}</p>}
        </div>
    );
}

export default TaxInput;
